import React, { useMemo, useState, useEffect, forwardRef } from 'react';
import { Map, ZoomControl, GeoJson, Overlay } from 'pigeon-maps';
import { formatAuditTime } from '_utils/arrival-audit';

// Belfast International Airport terminal
const BFS_CENTER = [54.6575, -6.2158];
const DEFAULT_ZOOM = 17;
const MIN_LABEL_GAP_MS = 5 * 60 * 1000;

export const LEG_COLORS = ['#2E86C1', '#E67E22', '#8E44AD', '#27AE60', '#C0392B', '#16A085'];

/** Centre + zoom that fits every point into a map of the given pixel size. */
const fitBounds = (points, width, height) => {
    if (!points.length) return { center: BFS_CENTER, zoom: DEFAULT_ZOOM };
    const lats = points.map((p) => p[0]);
    const lngs = points.map((p) => p[1]);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const center = [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
    const latRad = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
    const lngSpan = Math.max(maxLng - minLng, 0.0005);
    const latSpan = Math.max(latRad(maxLat) - latRad(minLat), 0.0005 * Math.PI / 180);
    const zoomX = Math.log2((width / 256) * (360 / lngSpan));
    const zoomY = Math.log2((height / 256) * ((2 * Math.PI) / latSpan));
    // -0.3 leaves a margin so edge markers and labels stay on screen
    const zoom = Math.floor(Math.min(zoomX, zoomY, 19) - 0.3);
    return { center, zoom: Math.max(12, zoom) };
};

/** Labels the first, last and one ping every 5 minutes so the map stays readable. */
const pickLabelled = (pings) => {
    const out = new Set();
    let lastMs = -Infinity;
    pings.forEach((p, i) => {
        if (i === 0 || i === pings.length - 1 || p.ms - lastMs >= MIN_LABEL_GAP_MS) {
            out.add(i);
            lastMs = p.ms;
        }
    });
    return out;
};

/**
 * Map of one passenger's GPS/beacon pings, one colour per leg (agent).
 * GPS = filled dot + path, beacon = square at the matched location's geofence centre.
 * `ref` is the element captured for the Excel export.
 */
const ArrivalAuditMap = forwardRef(({ legPings, width = 520, height = 320 }, ref) => {
    const mappable = useMemo(
        () =>
            legPings.map((lp, i) => ({
                ...lp,
                color: LEG_COLORS[i % LEG_COLORS.length],
                pings: lp.pings.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)),
            })),
        [legPings]
    );

    const fitted = useMemo(
        () => fitBounds(mappable.flatMap((lp) => lp.pings.map((p) => [p.lat, p.lng])), width, height),
        [mappable, width, height]
    );
    const [view, setView] = useState(fitted);
    useEffect(() => setView(fitted), [fitted]);

    const hasPoints = mappable.some((lp) => lp.pings.length);

    return (
        <div ref={ref} style={{ width, background: '#fff' }}>
            <div style={{ position: 'relative', width, height, border: '1px solid #D6E4F3' }}>
                <Map
                    width={width}
                    height={height}
                    center={view.center}
                    zoom={view.zoom}
                    onBoundsChanged={({ center, zoom }) => setView({ center, zoom })}
                    maxZoom={19}
                    minZoom={10}
                >
                    <ZoomControl />
                    {mappable.map((lp) => {
                        const gps = lp.pings.filter((p) => p.source === 'GPS');
                        if (gps.length < 2) return null;
                        return (
                            <GeoJson
                                key={`path-${lp.leg.taskId}`}
                                data={{
                                    type: 'FeatureCollection',
                                    features: [{
                                        type: 'Feature',
                                        geometry: { type: 'LineString', coordinates: gps.map((p) => [p.lng, p.lat]) },
                                        properties: {},
                                    }],
                                }}
                                styleCallback={() => ({ stroke: lp.color, strokeWidth: 3, fill: 'none', strokeOpacity: 0.8 })}
                            />
                        );
                    })}
                    {mappable.flatMap((lp) => {
                        const labelled = pickLabelled(lp.pings);
                        return lp.pings.map((p, i) => (
                            <Overlay key={`pt-${lp.leg.taskId}-${i}`} anchor={[p.lat, p.lng]} offset={[0, 0]}>
                                <div style={{ position: 'relative', pointerEvents: 'none' }}>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            transform: 'translate(-50%, -50%)',
                                            width: p.source === 'Beacon' ? 10 : 8,
                                            height: p.source === 'Beacon' ? 10 : 8,
                                            borderRadius: p.source === 'Beacon' ? 2 : '50%',
                                            background: p.source === 'Beacon' ? '#fff' : lp.color,
                                            border: `2px solid ${lp.color}`,
                                        }}
                                    />
                                    {labelled.has(i) && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                transform: 'translate(-50%, -150%)',
                                                background: lp.color,
                                                color: '#fff',
                                                padding: '1px 5px',
                                                borderRadius: 4,
                                                fontSize: 10,
                                                fontFamily: 'Arial, sans-serif',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {formatAuditTime(p.ms)}
                                        </div>
                                    )}
                                </div>
                            </Overlay>
                        ));
                    })}
                </Map>
                {!hasPoints && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(255,255,255,0.75)',
                            color: '#666',
                            fontSize: 13,
                            fontFamily: 'Arial, sans-serif',
                        }}
                    >
                        No mappable GPS/beacon pings in the task window
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '6px 4px', fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#333' }}>
                {mappable.map((lp) => (
                    <span key={`legend-${lp.leg.taskId}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: lp.color, display: 'inline-block' }} />
                        {lp.leg.type} – {lp.leg.employeeName}
                    </span>
                ))}
                <span style={{ color: '#666' }}>● GPS &nbsp; ▢ Beacon</span>
            </div>
        </div>
    );
});

export default ArrivalAuditMap;
